import { Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Auth } from '../auth';
import { LANGS, LangCode, setLang, storedLang } from '../i18n';
import { EnterpriseStore } from '../enterprise.store';
import { OnboardingService } from '../onboarding/onboarding.service';

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './layout.html',
})
export class Layout implements OnInit {
  private auth = inject(Auth);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);

  readonly user = this.auth.user;
  sidebarOpen = signal(false);

  // 多企业切换器
  readonly ent = inject(EnterpriseStore);
  private onbService = inject(OnboardingService);
  entOpen = signal(false);
  creatingEnterprise = signal(false);

  // 语言切换
  readonly langs = LANGS;
  langOpen = signal(false);
  currentLang = signal<LangCode>(storedLang());

  // 侧边栏菜单：section header（分组标题，仅 key+header，不可点）+ 组内一级项。
  // 普通项带独立 heroicon（outline，24×24；icon 为该图标的 path d 数组，支持多路径如齿轮）。
  nav: { key: string; route?: string | null; icon?: string[]; header?: boolean }[] = [
    // 仪表盘（置顶，无分组）
    { key: 'nav.dashboard', route: '/dashboard', icon: ['M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25'] },

    // 【虚拟卡】——全部占位页
    { key: 'nav.group.cards', header: true },
    { key: 'nav.cards.stored', route: '/cards/stored', icon: ['M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z'] },
    { key: 'nav.cards.credit', route: '/cards/credit', icon: ['M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z'] },
    { key: 'nav.cards.txns', route: '/cards/transactions', icon: ['M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z'] },
    { key: 'nav.cards.risk', route: '/cards/risk', icon: ['M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z'] },
    { key: 'nav.cards.holders', route: '/cards/holders', icon: ['M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z'] },

    // 【全球账户】——现有页面（accounts/transfer/transactions 从原扁平列表移入本组，不重复）
    { key: 'nav.group.global', header: true },
    { key: 'nav.accounts', route: '/accounts', icon: ['M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z'] },
    { key: 'nav.transfer', route: '/transfer', icon: ['M6 12 3.269 3.125A59.769 59.769 0 0121.485 12 59.768 59.768 0 013.27 20.875L5.999 12zm0 0h7.5'] },
    { key: 'nav.payees', route: '/payees', icon: ['M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zM10.5 15.75a3 3 0 00-6 0v.75c0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75v-.75z'] },
    { key: 'nav.transactions', route: '/transactions', icon: ['M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5'] },

    // 【数据统计】——占位页
    { key: 'nav.group.stats', header: true },
    { key: 'nav.reports', route: '/reports', icon: ['M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'] },
    { key: 'nav.statements', route: '/statements', icon: ['M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z'] },

    // 【其他】——现有页面
    { key: 'nav.group.other', header: true },
    { key: 'nav.users', route: '/users', icon: ['M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z'] },
    { key: 'nav.apikeys', route: '/apikeys', icon: ['M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z'] },
    { key: 'nav.settings', route: '/settings', icon: ['M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z', 'M15 12a3 3 0 11-6 0 3 3 0 016 0z'] },
  ];

  ngOnInit(): void {
    // 登录态下拉取企业列表（SSR 无 token，仅浏览器端加载）。
    if (isPlatformBrowser(this.platformId)) {
      void this.ent.load();
      // 从入驻新标签页切回 console 时刷新企业列表（拾取新建/续填后的变化）。
      window.addEventListener('focus', () => void this.ent.load(true));
    }
  }

  selectEnterprise(applicationId: string): void {
    this.ent.setActive(applicationId);
    this.entOpen.set(false);
  }

  // 新建/续填企业：生成免登录入驻链接 → 在新标签页打开独立入驻应用（不离开 console）。
  // 传 applicationId 则续填该草稿（不新建）；不传则新建一家。
  async createEnterprise(applicationId?: string): Promise<void> {
    if (this.creatingEnterprise()) return;
    this.entOpen.set(false);
    this.creatingEnterprise.set(true);
    // 先同步开空白新窗口（避免 await 后 window.open 被弹窗拦截），拿到链接再回填地址。
    const win = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    try {
      const reply = await this.onbService.createMyLink(
        applicationId ? { application_id: applicationId } : {},
      );
      if (reply.onboardingUrl) {
        if (win) win.location.href = reply.onboardingUrl;
        else if (typeof window !== 'undefined') window.open(reply.onboardingUrl, '_blank');
      } else if (win) {
        win.close();
      }
    } catch {
      if (win) win.close();
    } finally {
      this.creatingEnterprise.set(false);
    }
  }

  // 草稿/退回补件状态可续填（已提交/审核中/已通过不再编辑）。
  isDraftStatus(status?: string): boolean {
    return status === 'draft' || status === 'info_requested';
  }

  // 企业名兜底：法定名 → 申请号尾段 → 占位。
  enterpriseLabel(e: { legalName?: string; applicationId?: string } | null): string {
    if (!e) return '';
    if (e.legalName) return e.legalName;
    const id = e.applicationId ?? '';
    return id ? id.slice(-8) : '';
  }

  switchLang(code: LangCode): void {
    setLang(this.translate, code);
    this.currentLang.set(code);
    this.langOpen.set(false);
  }

  currentLangLabel(): string {
    return this.langs.find((l) => l.code === this.currentLang())?.label ?? 'English';
  }

  // 语言对应国旗（emoji）：英=🇬🇧 德=🇩🇪 台湾繁体=🇹🇼。
  langFlag(code: string): string {
    const m: Record<string, string> = { en: '🇬🇧', de: '🇩🇪', 'zh-TW': '🇹🇼' };
    return m[code] ?? '🌐';
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }
}
